'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useApiClient } from '../contexts/ApiContext';
import { useCentrifugo } from '../contexts/CentrifugoContext';
import { queryKeys } from '../lib/queryClient';
import {
  OpenGameJoinBlockedReasonEnum,
  OpenGameModeEnum,
  OpenGameTicketEnum,
  LiveGameModeEnum,
  LiveGameTicketEnum,
  type LiveGame,
  type OpenGame,
  type RushMidOpenSeat,
  type RushMidOpenSeatsGrid,
} from '../services/generated/Api';
import {
  arrayField,
  booleanField,
  literalField,
  numberField,
  numberLiteralField,
  rawField,
  stringField,
} from '../lib/jsonBody';

export function useOpenGames(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.rushMidOpenGames,
    queryFn: () => apiClient.api.rushMidControllerListOpenGames(),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    enabled,
  });
}

export function useLiveGames(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.rushMidLiveGames,
    queryFn: () => apiClient.api.rushMidControllerListLiveGames(),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
    refetchOnMount: 'always',
    enabled,
  });
}

export function useOpenGame(matchId: string | null, enabled = true) {
  const apiClient = useApiClient();

  return useQuery<OpenGame | null>({
    queryKey: queryKeys.rushMidOpenGame(matchId ?? ''),
    queryFn: () => apiClient.api.rushMidControllerGetOpenGame(matchId ?? ''),
    enabled: enabled && !!matchId,
    staleTime: 5 * 1000,
    refetchInterval: (query) =>
      query.state.status === 'error' || query.state.data === null ? false : 5 * 1000,
    refetchOnWindowFocus: (query) => query.state.data !== null,
    retry: false,
  });
}

const gamesChannel = (prefix: string): string => (prefix ? `${prefix}_rush_mid_games` : 'rush_mid_games');

const mergeJoinGate = (prev: OpenGame, next: OpenGame): OpenGame => {
  if (next.joinable !== undefined || prev.joinable === undefined) return next;
  return {
    ...next,
    joinable: prev.joinable,
    joinBlockedReason: prev.joinBlockedReason,
    joinableAt: prev.joinableAt,
  };
};

export const isJoinBlocked = (game: OpenGame, now = Date.now()): boolean =>
  game.joinable === false && (!game.joinableAt || now < new Date(game.joinableAt).getTime());

export type RushMidGamesEventType =
  | 'game_created'
  | 'game_joined'
  | 'game_left'
  | 'game_started'
  | 'game_playing'
  | 'game_closed'
  | 'game_finished';

export interface RushMidGamesEvent {
  type: RushMidGamesEventType;
  matchId?: string;
  open?: OpenGame;
  live?: LiveGame;
}

const GAMES_EVENT_TYPES: readonly RushMidGamesEventType[] = [
  'game_created',
  'game_joined',
  'game_left',
  'game_started',
  'game_playing',
  'game_closed',
  'game_finished',
];

const OPEN_TICKETS = Object.values(OpenGameTicketEnum).filter((v) => typeof v === 'number');
const OPEN_MODES = Object.values(OpenGameModeEnum).filter((v) => typeof v === 'number');
const LIVE_TICKETS = Object.values(LiveGameTicketEnum).filter((v) => typeof v === 'number');
const LIVE_MODES = Object.values(LiveGameModeEnum).filter((v) => typeof v === 'number');
const JOIN_BLOCKED_REASONS = Object.values(OpenGameJoinBlockedReasonEnum);

const parseSeat = (value: unknown): RushMidOpenSeat | null => {
  const userId = stringField(value, 'userId');
  const name = stringField(value, 'name');
  const level = numberField(value, 'level');
  if (userId === undefined || name === undefined || level === undefined) return null;
  return {
    userId,
    name,
    level,
    rating: numberField(value, 'rating'),
    partyGroup: numberField(value, 'partyGroup'),
  };
};

const parseSeatRow = (value: unknown, key: string): (RushMidOpenSeat | null)[] | undefined =>
  arrayField(value, key)?.map(parseSeat);

const parseSeats = (value: unknown): RushMidOpenSeatsGrid | undefined => {
  const grid = rawField(value, 'seats');
  const t = parseSeatRow(grid, 't');
  const ct = parseSeatRow(grid, 'ct');
  return t === undefined || ct === undefined ? undefined : { t, ct };
};

const parseOpenGame = (value: unknown): OpenGame | undefined => {
  const matchId = stringField(value, 'matchId');
  const ticket = numberLiteralField(value, 'ticket', OPEN_TICKETS);
  const mode = numberLiteralField(value, 'mode', OPEN_MODES);
  const map = stringField(value, 'map');
  const createdAt = stringField(value, 'createdAt');
  const seats = parseSeats(value);
  if (
    matchId === undefined ||
    ticket === undefined ||
    mode === undefined ||
    map === undefined ||
    createdAt === undefined ||
    seats === undefined
  ) {
    return undefined;
  }
  return {
    matchId,
    ticket,
    mode,
    map,
    createdAt,
    seats,
    botFillAt: stringField(value, 'botFillAt'),
    joinable: booleanField(value, 'joinable'),
    joinBlockedReason: literalField(value, 'joinBlockedReason', JOIN_BLOCKED_REASONS),
    joinableAt: stringField(value, 'joinableAt'),
  };
};

const parseLiveGame = (value: unknown): LiveGame | undefined => {
  const matchId = stringField(value, 'matchId');
  const ticket = numberLiteralField(value, 'ticket', LIVE_TICKETS);
  const mode = numberLiteralField(value, 'mode', LIVE_MODES);
  const map = stringField(value, 'map');
  const round = numberField(value, 'round');
  const seats = parseSeats(value);
  if (
    matchId === undefined ||
    ticket === undefined ||
    mode === undefined ||
    map === undefined ||
    round === undefined ||
    seats === undefined
  ) {
    return undefined;
  }
  return { matchId, ticket, mode, map, round, seats };
};

const parseGamesEvent = (data: unknown): RushMidGamesEvent | null => {
  const type = literalField(data, 'type', GAMES_EVENT_TYPES);
  if (type === undefined) return null;
  return {
    type,
    matchId: stringField(data, 'matchId'),
    open: parseOpenGame(rawField(data, 'open')),
    live: parseLiveGame(rawField(data, 'live')),
  };
};

export function useRushMidGamesSubscription() {
  const { subscribe, isConnected, channelPrefix } = useCentrifugo();
  const queryClient = useQueryClient();

  const handleEvent = useCallback(
    (data: unknown) => {
      const event = parseGamesEvent(data);
      if (!event) return;

      const patch = <T extends { matchId: string }>(
        key: readonly unknown[],
        fn: (games: T[]) => T[],
      ) => {
        const cur = queryClient.getQueryData<{ games: T[] }>(key);
        if (!cur) { queryClient.invalidateQueries({ queryKey: key }); return; }
        queryClient.setQueryData(key, { ...cur, games: fn(cur.games) });
      };
      const upsert = <T extends { matchId: string }>(games: T[], g: T): T[] => {
        const i = games.findIndex((x) => x.matchId === g.matchId);
        if (i === -1) return [...games, g];
        const next = games.slice();
        next[i] = g;
        return next;
      };
      const upsertOpen = (games: OpenGame[], g: OpenGame): OpenGame[] => {
        const i = games.findIndex((x) => x.matchId === g.matchId);
        if (i === -1) return [...games, g];
        const next = games.slice();
        next[i] = mergeJoinGate(games[i], g);
        return next;
      };
      const drop = <T extends { matchId: string }>(games: T[], id: string): T[] =>
        games.filter((x) => x.matchId !== id);

      switch (event.type) {
        case 'game_created':
        case 'game_joined':
        case 'game_left':
          if (event.open) patch<OpenGame>(queryKeys.rushMidOpenGames, (g) => upsertOpen(g, event.open!));
          else queryClient.invalidateQueries({ queryKey: queryKeys.rushMidOpenGames });
          break;
        case 'game_started':
        case 'game_closed':
          if (event.matchId) patch<OpenGame>(queryKeys.rushMidOpenGames, (g) => drop(g, event.matchId!));
          break;
        case 'game_playing':
          if (event.live) patch<LiveGame>(queryKeys.rushMidLiveGames, (g) => upsert(g, event.live!));
          else queryClient.invalidateQueries({ queryKey: queryKeys.rushMidLiveGames });
          break;
        case 'game_finished':
          if (event.matchId) patch<LiveGame>(queryKeys.rushMidLiveGames, (g) => drop(g, event.matchId!));
          break;
      }

      if (event.matchId) {
        const single = queryKeys.rushMidOpenGame(event.matchId);
        if (event.type === 'game_started' || event.type === 'game_closed') {
          queryClient.setQueryData(single, null);
        } else {
          queryClient.invalidateQueries({ queryKey: single });
        }
      }
    },
    [queryClient],
  );

  useEffect(() => {
    if (!isConnected) return;
    const channel = gamesChannel(channelPrefix);
    return subscribe(channel, handleEvent);
  }, [isConnected, channelPrefix, subscribe, handleEvent]);

  useEffect(() => {
    if (!isConnected) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.rushMidOpenGames });
    queryClient.invalidateQueries({ queryKey: queryKeys.rushMidLiveGames });
  }, [isConnected, queryClient]);

  return { isConnected };
}
